/**
 * ANA-R1-13 — dataset-open "Download all images now?" prompt.
 *
 * A pre-screener opening a data set almost always needs ALL images, but the
 * download action is buried in the burger menu. So on every open of a
 * not-fully-cached data set we ASK — explicitly NOT auto-download (the
 * quick-look path must stay cheap; AC2).
 *
 * ## Behaviour (locked decision OQ-A1-4 a)
 * - **Yes** = exactly the burger menu's "Download data set" action
 *   (`usePrefetchControls().downloadDataset()` — reused, never duplicated).
 * - **No** = dismiss; lazy loading continues unchanged.
 * - **"Don't ask again for this data set"** checkbox persists per user + per
 *   data set in localStorage (`datasetDownloadPromptPrefs`). No BE call.
 *
 * ## When it appears (AC1/AC3)
 * `shouldPromptDownload` gates on:
 *  - the surface being interactive (PROG-01 `isReady`) — so first paint is
 *    never delayed; the dialog mounts only after the readiness gate flips,
 *  - the data set having images,
 *  - the data set NOT being fully cached (live prefetch progress), and
 *  - the user not having suppressed it.
 * A per-mount latch makes it show AT MOST ONCE per open: dismissing with "No"
 * (or after deciding once) does not re-trigger while the surface stays mounted.
 *
 * Mounted by `AnalysisFeature` inside `PrefetchProvider`, main window only
 * (pop-outs share the origin-wide cache and are driven by the main window).
 */
import { useEffect, useState, useSyncExternalStore } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
} from "@mui/material";
import { useMe } from "@aeriosiq/auth-client";
import { compactButtonXsSx, compactFilledCtaSx } from "@aeriosiq/ui";
import { usePrefetchControls } from "./PrefetchProvider.js";
import { useAnalysisReadinessStore } from "../state/analysisReadinessStore.js";
import {
  datasetDownloadPromptPrefs,
  suppressionKey,
  ANONYMOUS_USER_ID,
} from "./datasetDownloadPromptPrefs.js";
import { shouldPromptDownload } from "./datasetDownloadPromptDecision.js";

export function DatasetDownloadPrompt({
  dataAcquisitionId,
}: {
  dataAcquisitionId: string;
}): React.ReactElement | null {
  const me = useMe();
  const userId = me.data?.id ?? ANONYMOUS_USER_ID;

  const prefetch = usePrefetchControls();
  const progress = prefetch?.progress;
  const cachedCount = progress?.cachedCount ?? 0;
  const totalCount = progress?.totalCount ?? 0;
  const hasImages = totalCount > 0;
  const fullyCached = hasImages && cachedCount >= totalCount;

  // AC3 — only consider prompting once the surface is interactive.
  const surfaceReady = useAnalysisReadinessStore((s) => s.isReady);

  // Suppression is per user + per data set; subscribe so a tick (this tab or
  // another) re-evaluates the decision, and read it off the live snapshot.
  const suppressedMap = useSyncExternalStore(
    datasetDownloadPromptPrefs.subscribe,
    datasetDownloadPromptPrefs.getSnapshot,
    datasetDownloadPromptPrefs.getSnapshot,
  );
  const suppressed =
    suppressedMap[suppressionKey(userId, dataAcquisitionId)] === true;

  const [open, setOpen] = useState(false);
  const [dontAsk, setDontAsk] = useState(false);
  // Show AT MOST ONCE per open: once decided (or shown) for this data set we
  // don't re-raise it while the surface stays mounted (e.g. images finish
  // downloading, or the user dismissed with "No").
  const [decided, setDecided] = useState(false);

  // Reset the per-data-set latch when the open data set changes (the surface
  // remounts on most DA switches, but be defensive about in-place id swaps).
  useEffect(() => {
    setDecided(false);
    setOpen(false);
    setDontAsk(false);
  }, [dataAcquisitionId]);

  const wantPrompt = shouldPromptDownload({
    fullyCached,
    suppressed,
    surfaceReady,
    hasImages,
  });

  useEffect(() => {
    if (decided) return;
    if (wantPrompt) {
      setOpen(true);
      setDecided(true);
    }
  }, [wantPrompt, decided]);

  const persistDontAsk = (): void => {
    if (dontAsk) {
      datasetDownloadPromptPrefs.suppress(userId, dataAcquisitionId);
    }
  };

  const onYes = (): void => {
    persistDontAsk();
    setOpen(false);
    prefetch?.downloadDataset();
  };

  const onNo = (): void => {
    persistDontAsk();
    setOpen(false);
  };

  if (!open) return null;

  // Rough remaining-image count, cheaply available from the live progress
  // (totalCount denominator − already-cached). Plain-language body.
  const remaining = Math.max(0, totalCount - cachedCount);
  const countPhrase =
    remaining > 0
      ? `This data set has about ${String(remaining)} image${
          remaining === 1 ? "" : "s"
        } left to download.`
      : "";

  return (
    <Dialog
      open={open}
      onClose={onNo}
      aria-labelledby="dataset-download-prompt-title"
      data-testid="dataset-download-prompt"
    >
      <DialogTitle id="dataset-download-prompt-title">
        Download all images now?
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Downloading all images now means they open instantly and stay
          available offline. You can also keep going — images load as you reach
          them.
          {countPhrase ? ` ${countPhrase}` : ""}
        </DialogContentText>
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Checkbox
              checked={dontAsk}
              onChange={(e) => {
                setDontAsk(e.target.checked);
              }}
              data-testid="dataset-download-prompt-dont-ask"
              size="small"
            />
          }
          label="Don't ask again for this data set"
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onNo}
          sx={compactButtonXsSx}
          data-testid="dataset-download-prompt-no"
        >
          Not now
        </Button>
        <Button
          variant="contained"
          onClick={onYes}
          // ANA-R4-UI-01 — primary CTA uses the 32px filled-CTA preset to match
          // the header's "Mark expert analysis complete" button (was 24px XS).
          sx={compactFilledCtaSx}
          data-testid="dataset-download-prompt-yes"
        >
          Download all images
        </Button>
      </DialogActions>
    </Dialog>
  );
}
